import { DealType } from "@/enums/dealType";
import { ClassType, Filter } from "@/types/SearchFilter.types";
import { initialFilterValue } from "@/utils/initialFilterValue";

// Keys that are expected in field[]=value format
const traditionalArrayFields = [
  "propertyType",
  "objectType",
  "rooms",
  "bedrooms",
  "bathrooms",
  "comfort", // Assuming comfort and similar are still field[]=value
  "multimedia",
  "landscape",
  "pets",
  "rentalOptions",
  "constructionStatus",
  "constructionCompletionDate",
  "numberOfApartments",
  "numberOfBuildings",
  "availabilityOfInstallments",
  "walls",
  "condition",
  "repair",
  "parking",
  "numberOfFloors",
  "additionally",
  "region",
  "city",
  "district",
  "microdistrict",
  "street",
  "metroStation",
  "propertyClass",
  "frame",
  // Realtor-specific arrays
  "exclusiveType",
  "heating",
  "insulation",
  "renovation",
  "elevator",
] as const;

// Keys that are now field[item]=true and should be parsed into string[]
const nestedDtoArrayKeys = new Set([
  "amenities",
  "communications",
  "appliances",
  "landscape",
  "infrastructure",
  "multimedia",
  "comfort",
  "pets",
  "rentalOptions",
]);

const numericFields = [
  "pricePerSqMMin",
  "pricePerSqMMax",
  "totalPriceMin",
  "totalPriceMax",
  "areaMin",
  "areaMax",
  "areaLandMin",
  "areaLandMax",
  "ceilingHeightMin",
  "ceilingHeightMax",
  "floorMin",
  "floorMax",
  "page",
  "limit",
] as const;

const booleanFields = [
  "notFirst",
  "notLast",
  "hasInstallment",
  "hasMortgage",
  "isNewBuilding",
  "isGoldenVisaEligible",
  "access24x7",
] as const; // Added hasInstallment if it's boolean

const stringFields = [
  "dealType", // Will be specifically handled for DealType enum
  "currency",
  "priceCurrency", // New field for price currency selection
  "titleOrDescription",
  "customId",
  "developerName",
  "developerLink",
  "projectName",
  "projectLink",
  // Realtor-specific simple strings
  "phone",
  "realtorId",
  // constructionStatus was in arrayFields, if it can also be a single string, adjust accordingly
  // For now, assuming it's primarily array via constructionStatus[]=value
] as const;

const parseArrayValue = (key: string, values: string[]): any[] => {
  // Ensure 'rooms', 'bedrooms', 'bathrooms' are treated as numbers if they come from traditionalArrayFields
  if (key === "rooms") {
    return values
      .map((v) => parseInt(v))
      .filter((v) => !isNaN(v) && v >= 1 && v <= 10);
  }

  if (key === "bedrooms") {
    return values
      .map((v) => parseInt(v))
      .filter((v) => !isNaN(v) && v >= 0 && v <= 10); // Allowing 0 for no bedrooms
  }

  if (key === "bathrooms") {
    return values
      .map((v) => parseInt(v))
      .filter((v) => !isNaN(v) && v >= 1 && v <= 10);
  }

  if (key === "propertyClass") {
    return values.filter((v) =>
      ["economy", "standard", "comfort", "business", "elite"].includes(v),
    ) as ClassType[];
  }

  if (key === "frame") {
    return values.filter((v) =>
      [
        "white_frame",
        "black_frame",
        "green_frame",
        "requires_renovation",
        "renovated", // Assuming renovated is also a valid frame type string
      ].includes(v),
    );
  }
  // For constructionStatus, if it's processed here via traditionalArrayFields (constructionStatus[]=value)
  if (key === "constructionStatus") {
    // Add validation if needed, e.g., ensure values are valid ConstructionStatus enum members
    return values;
  }

  return values; // Default: return as array of strings
};

export const parseQueryToFilters = (
  searchParams: URLSearchParams,
  initialDealType: DealType,
  defaultRegions: string[] = [],
): Filter => {
  const result: Filter = {
    ...initialFilterValue(initialDealType, defaultRegions),
  };

  // Initialize arrays for nested DTO keys
  nestedDtoArrayKeys.forEach((key) => {
    (result as any)[key] = [];
  });

  // Process nested DTO array keys (field[item]=true)
  for (const [paramName, paramValue] of searchParams.entries()) {
    const match = paramName.match(/^(\w+)\[([^\]]+)\]$/); // Matches key[item]
    if (match) {
      const baseKey = match[1];
      const item = match[2];
      if (nestedDtoArrayKeys.has(baseKey) && paramValue === "true") {
        ((result as any)[baseKey] as string[]).push(item);
      }
    }
  }

  // Process traditional array fields (field[]=value)
  traditionalArrayFields.forEach((field) => {
    const values = searchParams.getAll(`${field}[]`);
    if (values.length > 0) {
      (result as any)[field] = parseArrayValue(field, values);
    }
  });

  numericFields.forEach((field) => {
    const value = searchParams.get(field);
    if (value !== null) {
      // Changed from if(value) to allow "0"
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        (result as any)[field] = numValue;
      }
    }
  });

  booleanFields.forEach((field) => {
    const value = searchParams.get(field);
    if (value !== null) {
      (result as any)[field] = value === "true";
    }
  });

  stringFields.forEach((field) => {
    const value = searchParams.get(field);
    if (value !== null) {
      // Allow empty strings if they are valid values
      (result as any)[field] = value;
    }
  });

  // Ensure dealType is correctly set from DealType enum or defaults to initialDealType
  const dealTypeValue = searchParams.get("dealType");
  if (
    dealTypeValue &&
    Object.values(DealType).includes(dealTypeValue as DealType)
  ) {
    result.dealType = dealTypeValue as DealType;
  } else {
    result.dealType = initialDealType; // Fallback to initial if not present or invalid
  }

  return result;
};
